/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts"
import { FileText, Download, TrendingUp, Users, Shield, Calendar } from "lucide-react"

interface ReportData {
  id: string
  title: string
  type: string
  generatedDate: string
  generatedBy: string
  status: "completed" | "pending" | "failed"
  data: any
}

interface ReportViewPopupProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  report: ReportData | null
}

const COLORS = ["#000000", "#404040", "#808080", "#C0C0C0"]

export function ReportViewPopup({ open, onOpenChange, report }: ReportViewPopupProps) {
  if (!report) return null

  const handleExport = () => {
    // Simulate export functionality
    console.log("Exporting report:", report.id)
  }

  const renderCrimeStatsReport = () => (
    <div className="space-y-6">
      <Card className="border border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg text-black flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Crime Statistics Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={report.data.crimeStats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="caseType" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                  <Bar dataKey="count" fill="#000000" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={report.data.crimeStats}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ caseType, percentage }) => `${caseType}: ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {report.data.crimeStats.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}-${entry}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-black font-medium">Summary: {report.data.summary}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg text-black">Detailed Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="text-black font-semibold">Case Type</TableHead>
                <TableHead className="text-black font-semibold">Count</TableHead>
                <TableHead className="text-black font-semibold">Percentage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.data.crimeStats.map((item: any, index: number) => (
                <TableRow key={index}>
                  <TableCell className="font-medium text-black">{item.caseType}</TableCell>
                  <TableCell className="text-gray-600">{item.count}</TableCell>
                  <TableCell className="text-gray-600">{item.percentage}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )

  const renderOfficerPerformanceReport = () => (
    <div className="space-y-6">
      <Card className="border border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg text-black flex items-center gap-2">
            <Users className="h-5 w-5" />
            Officer Performance Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-black font-medium">Average Efficiency: {report.data.avgEfficiency}%</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="text-black font-semibold">Officer Name</TableHead>
                <TableHead className="text-black font-semibold">Cases Handled</TableHead>
                <TableHead className="text-black font-semibold">Cases Solved</TableHead>
                <TableHead className="text-black font-semibold">Efficiency</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.data.officers.map((officer: any, index: number) => (
                <TableRow key={index}>
                  <TableCell className="font-medium text-black">{officer.name}</TableCell>
                  <TableCell className="text-gray-600">{officer.cases}</TableCell>
                  <TableCell className="text-gray-600">{officer.solved}</TableCell>
                  <TableCell>
                    <Badge
                      className={
                        officer.efficiency >= 85
                          ? "bg-green-100 text-green-800"
                          : officer.efficiency >= 75
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                      }
                    >
                      {officer.efficiency}%
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )

  const renderStationAnalysisReport = () => (
    <div className="space-y-6">
      <Card className="border border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg text-black flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Police Station Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-black font-medium">Total Cases Across All Stations: {report.data.totalCases}</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="text-black font-semibold">Police Station</TableHead>
                <TableHead className="text-black font-semibold">Total Cases</TableHead>
                <TableHead className="text-black font-semibold">Officers</TableHead>
                <TableHead className="text-black font-semibold">Cases Solved</TableHead>
                <TableHead className="text-black font-semibold">Success Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.data.stations.map((station: any, index: number) => (
                <TableRow key={index}>
                  <TableCell className="font-medium text-black">{station.station}</TableCell>
                  <TableCell className="text-gray-600">{station.cases}</TableCell>
                  <TableCell className="text-gray-600">{station.officers}</TableCell>
                  <TableCell className="text-gray-600">{station.solved}</TableCell>
                  <TableCell>
                    <Badge
                      className={
                        (station.solved / station.cases) * 100 >= 85
                          ? "bg-green-100 text-green-800"
                          : (station.solved / station.cases) * 100 >= 75
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                      }
                    >
                      {Math.round((station.solved / station.cases) * 100)}%
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )

  const renderReportContent = () => {
    switch (report.type) {
      case "Crime Statistics":
        return renderCrimeStatsReport()
      case "Officer Performance":
        return renderOfficerPerformanceReport()
      case "Station Analysis":
        return renderStationAnalysisReport()
      default:
        return (
          <Card className="border border-gray-200">
            <CardContent className="p-8 text-center">
              <p className="text-gray-500">Report content will be displayed here</p>
            </CardContent>
          </Card>
        )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[90vw] max-w-none h-[90vh] bg-white flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-bold text-black flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {report.title}
            </DialogTitle>
            <Button onClick={handleExport} size="sm" className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4 min-h-0">
          <div className="space-y-4 pb-4">
            {/* Report Metadata */}
            <Card className="border border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Report Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Report Type</p>
                    <p className="text-black">{report.type}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Generated Date</p>
                    <p className="text-black">{new Date(report.generatedDate)?.toLocaleString() ?? "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Generated By</p>
                    <p className="text-black">{report.generatedBy}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Status</p>
                    <Badge
                      className={
                        report.status === "completed"
                          ? "bg-green-100 text-green-800"
                          : report.status === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                      }
                    >
                      {report.status}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Report Content */}
            {renderReportContent()}
          </div>
        </ScrollArea>

        <div className="flex-shrink-0 flex justify-end pt-4 border-t border-gray-200 bg-white">
          <Button onClick={() => onOpenChange(false)} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
